import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const statusMap = {
  New: "new",
  Processing: "processing",
  Shipped: "shipped",
};

export default function UserOrders() {
  const [activeTab, setActiveTab] = useState("New");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken") || "";
    const page = 1;
    const limit = 10; // per your example
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const s = statusMap[activeTab];
        const url = `${API_BASE_URL}/order`;
        const body = { page, limit, status: s };
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { token } : {}) },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        // Support multiple response shapes
        const list = json?.result?.orders || json?.result?.data || json?.result || json?.orders || json || [];
        // map to a simple shape (supporting latest `/api/order` response)
        const mapped = (list || []).map((o) => {
          // prefer orderItems (new API), fallback to older `items` shape
          const item = (o.orderItems && o.orderItems[0]) || (o.items && o.items[0]) || {};
          const product = item.product || o.product || {};

          const image = product?.product_media?.[0]?.url || item.image || "";
          const name = item.product_name || item.productName || product.product_name || item.name || "Product";
          const qty = Number(item.quantity ?? item.qty ?? o.quantity ?? 1);
          const price = Number(item.unit_price ?? item.unitPrice ?? item.total_price ?? o.final_amount ?? o.total_price ?? 0);

          return {
            id: String(o.id || o.order_number || o.orderId || o._id || ""),
            orderId: o.order_number || o.orderId || o._id || "",
            date: o.created_at ? new Date(o.created_at).toLocaleDateString() : (o.placedAt ? new Date(o.placedAt).toLocaleDateString() : (o.date || "")),
            product_name: name,
            price,
            qty,
            image,
            status: o.status || o.order_status || "",
            sellerName: o.seller?.name || "",
            raw: o,
          };
        });
        if (mounted) setOrders(mapped);
      } catch (err) {
        if (mounted) setError(err.message || "Failed to load orders");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchOrders();
    return () => { mounted = false; };
  }, [activeTab]);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">My Orders</h2>

      <div className="flex gap-3 mb-4">
        {["New", "Processing", "Shipped"].map((t) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-3 py-1 rounded ${activeTab===t ? "bg-green-700 text-white" : "bg-gray-100"}`}>
            {t}
          </button>
        ))}
      </div>

      {loading && <div>Loading {activeTab} orders…</div>}
      {error && <div className="text-red-600">Error: {error}</div>}
      {!loading && !error && orders.length === 0 && <div>No {activeTab} orders found.</div>}

      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="flex gap-4 p-3 border rounded bg-white">
            <img src={o.image || "/placeholder.png"} alt={o.product_name} className="w-20 h-20 object-cover rounded"/>
            <div className="flex-1">
              <div className="flex justify-between">
                <div>
                  <div className="font-semibold">{o.product_name}</div>
                  <div className="text-xs text-gray-500">Order#: {o.orderId} • {o.date}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold">₹{o.price}</div>
                  <div className="text-sm text-gray-600">Qty: {o.qty}</div>
                </div>
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => nav(`/order/${o.id}`, { state: { order: o }})}
                        className="px-3 py-1 bg-gray-800 text-white rounded text-sm">
                  View details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}